<template>
  <div class="modal show">
    <div class="modal-content">
      <span class="close" @click="$emit('close')">&times;</span>
      <h2>{{ t('createDisk.title') }}</h2>

      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="diskName">{{ t('createDisk.name') }}:</label>
          <input
            type="text"
            id="diskName"
            v-model="formData.diskName"
            required
          >
        </div>

        <div class="form-group">
          <label for="diskZone">{{ currentProvider === 'aws' ? t('createDisk.region') : t('createDisk.zone') }}:</label>
          <select id="diskZone" v-model="formData.zone" required>
            <option value="">{{ t('common.loading') }}</option>
            <!-- GCP Zones -->
            <option v-if="currentProvider === 'gcp'" value="asia-northeast1-a">asia-northeast1-a</option>
            <option v-if="currentProvider === 'gcp'" value="asia-northeast1-b">asia-northeast1-b</option>
            <option v-if="currentProvider === 'gcp'" value="asia-northeast1-c">asia-northeast1-c</option>
            <!-- AWS Availability Zones -->
            <option v-if="currentProvider === 'aws'" value="ap-northeast-1a">ap-northeast-1a</option>
            <option v-if="currentProvider === 'aws'" value="ap-northeast-1c">ap-northeast-1c</option>
            <option v-if="currentProvider === 'aws'" value="ap-northeast-1d">ap-northeast-1d</option>
          </select>
        </div>

        <div class="form-group">
          <label for="diskSize">{{ t('createDisk.size') }}:</label>
          <div class="slider-input-group">
            <input
              type="range"
              id="diskSizeSlider"
              v-model.number="formData.sizeGb"
              min="256"
              max="2048"
              class="slider"
            >
            <input
              type="number"
              id="diskSize"
              v-model.number="formData.sizeGb"
              min="256"
              max="2048"
              class="number-input"
              required
            >
          </div>
        </div>

        <div class="form-group">
          <label for="diskType">{{ t('createDisk.type') }}:</label>
          <select id="diskType" v-model="formData.diskType" required>
            <!-- GCP Disk Types -->
            <option v-if="currentProvider === 'gcp'" value="pd-standard">{{ t('createDisk.types.standard') }}</option>
            <option v-if="currentProvider === 'gcp'" value="pd-ssd">{{ t('createDisk.types.ssd') }}</option>
            <option v-if="currentProvider === 'gcp'" value="pd-balanced">{{ t('createDisk.types.balanced') }}</option>
            <!-- AWS EBS Volume Types -->
            <option v-if="currentProvider === 'aws'" value="gp2">General Purpose SSD (gp2)</option>
            <option v-if="currentProvider === 'aws'" value="gp3">General Purpose SSD (gp3)</option>
            <option v-if="currentProvider === 'aws'" value="io1">Provisioned IOPS SSD (io1)</option>
            <option v-if="currentProvider === 'aws'" value="io2">Provisioned IOPS SSD (io2)</option>
          </select>
        </div>

        <div class="cost-prediction">
          <p>{{ t('createDisk.estimatedMonthlyCost') }}: <strong>${{ estimatedCost }}</strong></p>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn btn-success" :disabled="loading">
            {{ loading ? t('createDisk.creating') : t('createDisk.create') }}
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
                          {{ t('createDisk.cancel') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResources } from '../composables/useResources';
import { useCloudProvider } from '../composables/useCloudProvider.js';

export default {
  name: 'CreateDiskModal',
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { createDisk } = useResources();
    const { currentProvider } = useCloudProvider();
    const loading = ref(false);

    const formData = reactive({
      diskName: '',
      zone: currentProvider.value === 'aws' ? 'ap-northeast-1a' : 'asia-northeast1-a',
      sizeGb: 256,
      diskType: currentProvider.value === 'aws' ? 'gp2' : 'pd-standard'
    });

    // 磁盘类型价格映射 (每GB每月)
    const diskPrices = {
      // GCP磁盘类型
      'pd-standard': 0.052,
      'pd-ssd': 0.221,
      'pd-balanced': 0.13,
      // AWS EBS卷类型
      'gp2': 0.10,
      'gp3': 0.08,
      'io1': 0.125,
      'io2': 0.125,
      'sc1': 0.025,
      'st1': 0.045
    };

    // 计算预计月度费用
    const estimatedCost = computed(() => {
      const pricePerGb = diskPrices[formData.diskType] || 0;
      const totalCost = formData.sizeGb * pricePerGb;
      return totalCost.toFixed(2);
    });

    const handleSubmit = async () => {
      loading.value = true;
      try {
        // 根据云提供商映射字段
        let diskData = { ...formData };

        if (currentProvider.value === 'aws') {
          // AWS字段映射
          diskData = {
            volumeName: formData.diskName,
            size: formData.sizeGb,
            volumeType: formData.diskType,
            zone: formData.zone
          };
        } else {
          // GCP字段映射（保持原有格式）
          diskData = {
            diskName: formData.diskName,
            sizeGb: formData.sizeGb,
            diskType: formData.diskType,
            zone: formData.zone
          };
        }

        await createDisk(diskData);
        emit('created');
      } catch (error) {
        console.error(t('createDisk.createFailed'), error);
        alert(error.message);
      } finally {
        loading.value = false;
      }
    };

    return {
      formData,
      loading,
      estimatedCost,
      handleSubmit,
      currentProvider,
      t
    };
  }
};
</script>

<style scoped>
.slider-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}
.slider {
  flex: 1;
}
.number-input {
  width: 80px;
}
.cost-prediction {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;
  text-align: center;
}
.cost-prediction p {
  margin: 0;
  color: #495057;
}
.cost-prediction strong {
  color: #28a745;
  font-size: 1.1em;
}
</style>
